import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Copy, Users, Trophy, Loader2, Globe, ShieldCheck, Layers, Trash2, PieChart, X, Sliders } from "lucide-react";

const F = (n, d = 2) => (n === null || n === undefined ? "—" : Number(n).toFixed(d));

export default function CopyTraders() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [groups, setGroups] = useState([]);
  const [showGroupBuilder, setShowGroupBuilder] = useState(false);
  const [rebalanceGroup, setRebalanceGroup] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get(`/community/bots?limit=30${verifiedOnly ? "&verified_only=true" : ""}`).then((r) => setBots(r.data.bots || []))
      .finally(() => setLoading(false));
  };
  const loadGroups = () => {
    api.get("/copy/groups").then((r) => setGroups(r.data.groups || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [verifiedOnly]);
  useEffect(() => { loadGroups(); }, []);

  const copy = async (bot) => {
    setCopying(bot.id);
    try {
      const cash = window.prompt(`Starting cash for your copy of "${bot.name}"?`, "5000");
      if (!cash) return;
      const { data } = await api.post("/bots/copy", {
        source_bot_id: bot.id, mode: "copy_config",
        starting_cash: parseFloat(cash),
      });
      toast.success(`Copied — ${data.name}`, {
        action: { label: "Open bots", onClick: () => navigate("/bots") },
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Copy failed"); }
    finally { setCopying(""); }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? Stops all ${(g.bot_ids || []).length} member bots.`)) return;
    try {
      await api.delete(`/copy/groups/${g.id}`);
      toast.success("Group deleted");
      loadGroups();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="copy-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Community</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-2">
            <Trophy className="w-7 h-7 text-[hsl(var(--ai))]" /> Copy Traders
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Discover the strategies other traders have made public. Copy a single bot or build a diversified follow-group with allocation splits.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowGroupBuilder(true)}
            data-testid="new-copy-group-btn"
            variant="outline"
            className="rounded-full"
          >
            <Layers className="w-3.5 h-3.5 mr-1.5" /> New group
          </Button>
          <button
            onClick={() => setVerifiedOnly((v) => !v)}
            data-testid="copy-verified-filter"
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
              verifiedOnly ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5" /> Verified only
          </button>
        </div>
      </div>

      {/* Existing follow-groups */}
      {groups.length > 0 && (
        <Card className="p-5 rounded-2xl" data-testid="copy-groups-list">
          <div className="flex items-center gap-2 mb-3">
            <PieChart className="w-4 h-4 text-[hsl(var(--ai))]" />
            <span className="text-sm font-display font-semibold">Your follow-groups</span>
            <span className="text-xs text-muted-foreground">— multi-publisher allocations</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {groups.map((g) => (
              <Card key={g.id} className="p-3 rounded-xl border-border/60" data-testid={`copy-group-${g.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-medium text-sm">{g.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      ${(g.starting_cash || 0).toLocaleString()} across {(g.allocations || []).length} bots
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setRebalanceGroup(g)} className="rounded-full"
                            data-testid={`copy-group-rebalance-${g.id}`} title="Rebalance allocation">
                      <Sliders className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteGroup(g)} className="rounded-full text-[hsl(var(--loss))]"
                            data-testid={`copy-group-delete-${g.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {(g.bots || []).map((b) => {
                    const alloc = (g.allocations || []).find((a) => a.source_bot_id === b.copied_from);
                    return (
                      <div key={b.id || b.copied_from} className="flex items-center justify-between text-xs" data-testid={`group-bot-${b.copied_from}`}>
                        <div className="truncate flex-1">
                          <span className="font-mono-tab text-muted-foreground w-9 inline-block">{alloc ? `${alloc.pct.toFixed(0)}%` : "—"}</span>
                          <span className="ml-1">{b.name}</span>
                        </div>
                        <span className={`font-mono-tab ${(b.verified_return_pct || 0) >= 0 ? "gain" : "loss"}`}>
                          {b.verified_return_pct != null ? `${b.verified_return_pct >= 0 ? "+" : ""}${F(b.verified_return_pct)}%` : "—"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5 rounded-2xl" data-testid="copy-list">
        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : bots.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center" data-testid="copy-empty">
            <Globe className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
            No public bots yet. Be the first — publish yours from the Auto-Trade page.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {bots.map((b, i) => (
              <Card key={b.id} className="p-4 rounded-xl border-border/60 hover:border-border transition-colors" data-testid={`copy-card-${i}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="font-medium flex items-center gap-1.5">
                      {b.name}
                      {b.is_verified && (
                        <span title="Return backed by tamper-evident snapshot chain" data-testid={`copy-verified-${i}`}>
                          <ShieldCheck className="w-4 h-4 text-[hsl(var(--gain))]" />
                        </span>
                      )}
                      {b.tier && (
                        <span data-testid={`copy-tier-${i}`}
                              title={`${b.tier} — ${b.chain_length} snapshots across ${b.chain_days}d`}
                              className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full ${
                                b.tier === "platinum" ? "bg-gradient-to-r from-slate-200 to-slate-400 text-slate-900" :
                                b.tier === "gold" ? "bg-amber-500/90 text-amber-950" :
                                b.tier === "silver" ? "bg-slate-400/80 text-slate-900" :
                                "bg-orange-800/70 text-orange-100"
                              }`}>
                          {b.tier}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mt-0.5">{b.strategy}</div>
                  </div>
                  <Badge variant="secondary" className="rounded-full text-[10px]">{(b.symbols || []).join(", ") || "—"}</Badge>
                </div>
                {b.tagline && <div className="text-xs text-muted-foreground italic mb-3">"{b.tagline}"</div>}
                <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                  <Stat label="Return" val={b.ranking_return_pct != null ? `${b.ranking_return_pct >= 0 ? "+" : ""}${F(b.ranking_return_pct)}%` : "—"}
                        cls={b.ranking_return_pct >= 0 ? "gain" : "loss"} />
                  <Stat label="Route" val={b.route || "sim"} />
                  <Stat label="Start" val={b.starting_equity ? `$${(b.starting_equity / 1000).toFixed(0)}k` : "—"} />
                </div>
                <Button onClick={() => copy(b)} disabled={copying === b.id}
                        data-testid={`copy-btn-${i}`} className="rounded-full w-full">
                  {copying === b.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  Copy to my account
                </Button>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {showGroupBuilder && (
        <GroupBuilder
          allBots={bots}
          onClose={() => setShowGroupBuilder(false)}
          onCreated={() => { setShowGroupBuilder(false); loadGroups(); toast.success("Follow-group created — bots are running"); }}
        />
      )}
      {rebalanceGroup && (
        <RebalanceModal
          group={rebalanceGroup}
          onClose={() => setRebalanceGroup(null)}
          onSaved={() => { setRebalanceGroup(null); loadGroups(); toast.success("Group rebalanced — slices updated live"); }}
        />
      )}
    </div>
  );
}

const Stat = ({ label, val, cls = "" }) => (
  <div>
    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`font-mono-tab text-sm ${cls}`}>{val}</div>
  </div>
);

function GroupBuilder({ allBots, onClose, onCreated }) {
  const [name, setName] = useState("Diversified follows");
  const [startingCash, setStartingCash] = useState(10000);
  const [picks, setPicks] = useState([]);   // [{source_bot_id, pct, name}]
  const [saving, setSaving] = useState(false);

  const totalPct = picks.reduce((s, p) => s + Number(p.pct || 0), 0);
  const validPct = Math.abs(totalPct - 100) <= 1 && picks.length >= 1;

  const togglePick = (b) => {
    setPicks((cur) => {
      const found = cur.find((x) => x.source_bot_id === b.id);
      if (found) return cur.filter((x) => x.source_bot_id !== b.id);
      // Auto-distribute evenly among all picks + the new one
      const next = [...cur, { source_bot_id: b.id, pct: 0, name: b.name }];
      const even = Math.floor(100 / next.length);
      const rem = 100 - even * next.length;
      return next.map((x, i) => ({ ...x, pct: i === 0 ? even + rem : even }));
    });
  };
  const setPct = (source_bot_id, pct) => setPicks((cur) =>
    cur.map((p) => (p.source_bot_id === source_bot_id ? { ...p, pct: Math.max(0, Math.min(100, Number(pct) || 0)) } : p))
  );
  const removePick = (source_bot_id) => setPicks((cur) => cur.filter((p) => p.source_bot_id !== source_bot_id));

  const equalize = () => {
    if (picks.length === 0) return;
    const even = Math.floor(100 / picks.length);
    const rem = 100 - even * picks.length;
    setPicks(picks.map((p, i) => ({ ...p, pct: i === 0 ? even + rem : even })));
  };

  const submit = async () => {
    if (!validPct) return toast.error(`Allocations must sum to 100% (got ${totalPct.toFixed(0)}%)`);
    setSaving(true);
    try {
      await api.post("/copy/groups", {
        name: name.trim() || "Group",
        starting_cash: parseFloat(startingCash),
        allocations: picks.map((p) => ({ source_bot_id: p.source_bot_id, pct: Number(p.pct) })),
      });
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="group-builder-modal">
      <Card className="max-w-2xl w-full max-h-[92vh] overflow-y-auto border-border rounded-2xl"
            onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-muted-foreground">Copy trading</div>
            <div className="font-display text-2xl font-bold flex items-center gap-2">
              <Layers className="w-6 h-6" /> New follow-group
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="group-builder-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Group name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="Diversified follows" data-testid="group-name" className="rounded-lg" />
            </div>
            <div>
              <Label className="text-xs">Starting cash</Label>
              <Input type="number" min="100" step="100" value={startingCash}
                     onChange={(e) => setStartingCash(e.target.value)}
                     data-testid="group-cash" className="rounded-lg font-mono-tab" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Pick publishers to follow</Label>
            <div className="mt-2 grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto pr-1 border border-border/60 rounded-lg p-2">
              {allBots.length === 0 ? (
                <div className="text-xs text-muted-foreground p-2">No public bots yet.</div>
              ) : allBots.map((b) => {
                const on = picks.some((p) => p.source_bot_id === b.id);
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => togglePick(b)}
                    data-testid={`group-pick-${b.id}`}
                    className={`text-left px-3 py-1.5 rounded-md flex items-center justify-between transition-colors ${
                      on ? "bg-foreground text-background" : "hover:bg-secondary"
                    }`}
                  >
                    <span className="text-sm truncate">{b.name}</span>
                    <span className="text-[10px] font-mono-tab opacity-70">
                      {b.ranking_return_pct != null ? `${b.ranking_return_pct >= 0 ? "+" : ""}${F(b.ranking_return_pct)}%` : "—"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {picks.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Allocation split (must total 100%)</Label>
                <Button variant="ghost" size="sm" onClick={equalize} data-testid="group-equalize" className="rounded-full text-xs">
                  Equalize
                </Button>
              </div>
              <div className="space-y-2">
                {picks.map((p) => (
                  <div key={p.source_bot_id} className="flex items-center gap-2" data-testid={`group-alloc-${p.source_bot_id}`}>
                    <span className="text-sm flex-1 truncate">{p.name}</span>
                    <Input type="number" min="1" max="100" step="1" value={p.pct}
                           onChange={(e) => setPct(p.source_bot_id, e.target.value)}
                           data-testid={`group-alloc-input-${p.source_bot_id}`}
                           className="w-20 h-8 rounded-lg font-mono-tab text-right" />
                    <span className="text-xs text-muted-foreground">%</span>
                    <button onClick={() => removePick(p.source_bot_id)} data-testid={`group-remove-${p.source_bot_id}`}
                            className="text-muted-foreground hover:text-[hsl(var(--loss))]">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className={`text-xs mt-2 font-mono-tab ${validPct ? "gain" : "loss"}`} data-testid="group-total-pct">
                Total: {totalPct.toFixed(0)}% {validPct ? "✓" : "(must be 100%)"}
              </div>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving || !validPct}
                  data-testid="group-create-btn"
                  className="rounded-full">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
            Create follow-group
          </Button>
        </div>
      </Card>
    </div>
  );
}

function RebalanceModal({ group, onClose, onSaved }) {
  // Initialize from group's current allocations
  const initial = (group.allocations || []).map((a) => {
    const bot = (group.bots || []).find((b) => b.copied_from === a.source_bot_id);
    return {
      source_bot_id: a.source_bot_id,
      pct: Number(a.pct) || 0,
      name: bot?.name || "?",
    };
  });
  const [picks, setPicks] = useState(initial);
  const [saving, setSaving] = useState(false);

  const total = picks.reduce((s, p) => s + Number(p.pct || 0), 0);
  const validTotal = Math.abs(total - 100) <= 1;

  const setPct = (source_bot_id, pct) => setPicks((cur) =>
    cur.map((p) => (p.source_bot_id === source_bot_id ? { ...p, pct: Math.max(0, Math.min(100, Number(pct) || 0)) } : p))
  );

  const equalize = () => {
    if (picks.length === 0) return;
    const even = Math.floor(100 / picks.length);
    const rem = 100 - even * picks.length;
    setPicks(picks.map((p, i) => ({ ...p, pct: i === 0 ? even + rem : even })));
  };

  const save = async () => {
    if (!validTotal) return toast.error(`Must total 100% (got ${total.toFixed(0)}%)`);
    setSaving(true);
    try {
      await api.patch(`/copy/groups/${group.id}`, {
        allocations: picks.map((p) => ({ source_bot_id: p.source_bot_id, pct: Number(p.pct) })),
      });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebalance failed");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="rebalance-modal">
      <Card className="max-w-lg w-full max-h-[90vh] overflow-y-auto border-border rounded-2xl"
            onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-muted-foreground">Rebalance</div>
            <div className="font-display text-xl font-bold flex items-center gap-2">
              <Sliders className="w-5 h-5" /> {group.name}
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="rebalance-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-muted-foreground">
            Drag the sliders to shift capital between publishers. Each bot's <span className="font-semibold text-foreground">starting_equity</span> is recomputed proportionally so mirror-signal sizing tracks the new split.
          </div>
          <div className="space-y-3">
            {picks.map((p) => (
              <div key={p.source_bot_id} className="space-y-1" data-testid={`rebalance-row-${p.source_bot_id}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate flex-1">{p.name}</span>
                  <span className="font-mono-tab text-sm w-14 text-right font-bold">{Number(p.pct).toFixed(0)}%</span>
                </div>
                <input
                  type="range" min={0} max={100} step={1} value={p.pct}
                  onChange={(e) => setPct(p.source_bot_id, e.target.value)}
                  data-testid={`rebalance-slider-${p.source_bot_id}`}
                  className="w-full accent-[hsl(var(--gain))]"
                />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-2">
            <button onClick={equalize} data-testid="rebalance-equalize"
                    className="text-xs text-muted-foreground hover:text-foreground underline">
              Equalize
            </button>
            <div className={`text-sm font-mono-tab ${validTotal ? "gain" : "loss"}`} data-testid="rebalance-total">
              Total: {total.toFixed(0)}% {validTotal ? "✓" : "(must be 100%)"}
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving || !validTotal}
                  data-testid="rebalance-save-btn" className="rounded-full">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sliders className="w-4 h-4 mr-2" />}
            Rebalance
          </Button>
        </div>
      </Card>
    </div>
  );
}

